import { AutoComplete } from '@services/MarppleShop/features/Search/type/autocomplete';
import { initCancelTokenHelper } from '../helper/initCancelTokenHelper.ts';

export interface AutoCompleteConfig {
  minCharsForAutoComplete?: number;
  customCondition?(value: string, alreadySubmitted: boolean): boolean;
}

export interface AutoCompleteCallbacks {
  onInputChange?: (value?: string) => void;

  onSubmit?: (search: string | undefined) => void;

  // 자동완성 데이터 요청 콜백 추가
  onRequestAutoComplete: (value?: string) => Promise<AutoComplete[]>;
}

export interface AutoCompleteState {
  // 현재 검색어
  search?: string;

  // 이전 검색어
  prevSearch?: string;

  // submit 한 번만 허용하기 위한 플래그인데, 이것도 옵션으로 필요하면 말해주세요.
  alreadySubmitted?: boolean;
}

interface IAutoCompleteController {
  // 검색어 getter
  getSearch(): string | undefined;

  // 검색어 setter
  setSearch(value: string | undefined): void;

  // config 에 명시한 조건에 따라 자동완성 활성화 조건 뱉음
  canAutoComplete(): boolean;

  // alreadySubmitted 초기화
  resetSubmitState(): void;

  // 이전 요청 취소
  cancelPrevRequest(): void;
}

export class AutoCompleteController implements IAutoCompleteController {
  private readonly cancelTokenHelper = initCancelTokenHelper();

  constructor(
    private readonly state: AutoCompleteState,
    private readonly callbacks: AutoCompleteCallbacks,
    private readonly config: AutoCompleteConfig,
  ) {}

  public getSearch(): string | undefined {
    return this.state.search;
  }

  public setSearch(value: string | undefined): void {
    // 이전 순위 업데이트
    this.state.prevSearch = this.state.search;
    this.state.search = value;
  }

  public async handleAutoComplete(): Promise<AutoComplete[] | undefined> {
    if (!this.canAutoComplete()) {
      return;
    }

    // 검색어 같으면 아무 일도 하지 않음
    if (this.isEqualWithPrevSearch(this.state.search)) {
      return;
    }

    // 이전 검색어 업데이트
    this.setSearch(this.state.search);

    if (this.callbacks.onInputChange) {
      this.callbacks.onInputChange(this.state.search);
    }

    if (this.callbacks.onRequestAutoComplete) {
      // 이전에 전송된 요청이 있다면 취소하고 cancel token 재설정
      this.cancelTokenHelper.cancelPrevRequest();
      this.cancelTokenHelper.setCancelToken();

      const auto_completes = await this.callbacks.onRequestAutoComplete(this.state.search);
      return auto_completes;
    }
  }

  public handleSubmit(): void {
    if (this.callbacks.onSubmit) {
      this.callbacks.onSubmit(this.state.search);
    }
    this.state.alreadySubmitted = true;
  }

  public canAutoComplete(): boolean {
    const customPredicated: boolean =
      !!this.config.customCondition &&
      this.config.customCondition(this.state.search ?? '', !!this.state.alreadySubmitted);

    const isGreaterThanMinChars: boolean =
      !!this.config.minCharsForAutoComplete &&
      (this.state.search ?? '').length >= this.config.minCharsForAutoComplete;

    return customPredicated && isGreaterThanMinChars;
  }

  public resetSubmitState(): void {
    this.state.alreadySubmitted = false;
  }

  // 이전 검색어와 같은지 확인하는 함수
  public isEqualWithPrevSearch(value: string | undefined): boolean {
    return this.state.prevSearch === value;
  }

  public cancelPrevRequest(): void {
    this.cancelTokenHelper.cancelPrevRequest();
  }
}
